import type { MouseEvent } from "react";
import { Tooltip } from "@mui/material";
import cx from "classnames";
import Pit from "src/business-components/Pit/Pit";
import WrapIf from "src/components/utils/WrapIf/WrapIf";
import type { PitSelectorDefaultRenderOptionProps } from "../../PitSelector.types";
import { getLeftRailStyleOverride } from "../../PitSelector.utils";

const PitSelectorDefaultRenderOption = ({
  option,
  onClick,
  slotProps,
  slots,
  component: Root = "li",
  ...props
}: PitSelectorDefaultRenderOptionProps) => {
  const EndAdornment = slots?.option?.endAdornment;
  const LeftRail = slots?.option?.leftRail;

  // When a left-rail slot is provided, internally carve out the gutter so the
  // rail can sit outside the <li>'s background (MUI's hover/selected/Mui-focused
  // bg doesn't paint over the rail). React-window's per-row inline style sets
  // `top` / `height` / `transform` / `position` — we layer the override on top
  // without touching those.
  const liStyle = LeftRail
    ? { ...props.style, ...getLeftRailStyleOverride() }
    : props.style;

  // MUI sets `aria-selected` on the <li> for the option matching `value`. React
  // may forward it as `boolean` or the stringified attribute, so handle both.
  const ariaSelected = props["aria-selected"];
  const isSelected = ariaSelected === true || ariaSelected === "true";
  let tooltipContent = option.tooltipContent;

  if (option.isRerun) {
    tooltipContent = "Part of a re-run sequence";
  }

  const rowClassName = cx(
    "flex items-center",
    {
      "pl-8!": option.isRerun,
    },
    props.className,
    slotProps?.option?.className
  );

  return (
    <WrapIf
      condition={!!tooltipContent}
      With={Tooltip}
      arrow
      placement="right"
      title={tooltipContent}
    >
      <Root
        {...props}
        style={liStyle}
        className={rowClassName}
        onClick={(e: MouseEvent<HTMLLIElement>) => {
          onClick?.(e, option);
        }}
      >
        {LeftRail && <LeftRail {...option} isSelected={isSelected} />}
        <Pit
          {...option}
          {...slotProps?.renderOption?.Pit}
          onlySubtitle={option.isRerun}
          className={cx("flex-1", option.isRerun ? "h-full" : undefined)}
          slotProps={{
            PitSubtitle: {
              tooltipTitle: option.isRerun ? "Previous run's start time" : undefined,
            },
          }}
          // app_pit={renderedAppPit}
        />
        {EndAdornment && (
          <EndAdornment {...option} {...(slotProps?.option?.endAdornment as any)} />
        )}
      </Root>
    </WrapIf>
  );
};

export default PitSelectorDefaultRenderOption;
